import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CHAVE } from './dados';
import type { TipoReuniao } from './tipos';

/** Lote 19 (12e): o que o workspace oferece de reunião on-line (servidor da sala própria e recursos com Google Meet). */
export interface ConfigReuniao {
  sala_base: string | null;
  meet: { conectado: boolean; recursos: string[] };
}

const CHAVE_REUNIAO = [CHAVE, 'reuniao'] as const;
export const useConfigReuniao = () => useQuery({ queryKey: CHAVE_REUNIAO, queryFn: () => get<ConfigReuniao>('/agenda/reuniao'), staleTime: 30_000 });

const TIPOS: TipoReuniao[] = ['nenhuma', 'google_meet', 'sala_propria', 'link'];

/**
 * Seletor "Reunião on-line" do agendamento: Nenhuma, Google Meet, Sala da Malgo ou Link próprio (o campo de texto). O Meet
 * aparece desabilitado sem o Google Calendar conectado ou sem a agenda do recurso; a sala, sem o servidor configurado. O
 * tipo que o agendamento já tem fica sempre escolhível (o servidor não reconfere quem não troca de tipo).
 */
export function CampoReuniao({
  idBase,
  recursoId,
  tipo,
  aoTipo,
  link,
  aoLink,
  atual,
}: {
  idBase: string;
  recursoId: string;
  tipo: TipoReuniao;
  aoTipo: (t: TipoReuniao) => void;
  link: string;
  aoLink: (v: string) => void;
  /** Tipo gravado (na ficha): continua disponível mesmo que o conector ou o servidor tenham saído. */
  atual?: TipoReuniao;
}) {
  const { t } = useTranslation('agenda');
  const cfg = useConfigReuniao();
  const semConector = !cfg.data?.meet.conectado;
  const semAgenda = !semConector && !!recursoId && !cfg.data?.meet.recursos.includes(recursoId);
  const meetOff = atual !== 'google_meet' && (semConector || !recursoId || semAgenda);
  const salaOff = atual !== 'sala_propria' && !cfg.data?.sala_base;
  const off: Record<TipoReuniao, boolean> = { nenhuma: false, link: false, google_meet: meetOff, sala_propria: salaOff };

  // A opção escolhida que deixou de valer (recurso trocado para um sem agenda do Google) volta para "Nenhuma".
  useEffect(() => {
    if (cfg.data && off[tipo]) aoTipo('nenhuma');
  });

  const motivos = [
    meetOff && (semConector ? t('reuniao.semConector') : !recursoId ? t('reuniao.escolhaRecurso') : t('reuniao.semAgenda')),
    salaOff && t('reuniao.semSala'),
  ].filter(Boolean) as string[];
  const ajuda = tipo === 'google_meet' ? t('reuniao.meetAjuda') : tipo === 'sala_propria' && atual !== 'sala_propria' ? t('reuniao.salaAjuda') : motivos.join(' ') || undefined;

  return (
    <>
      <div className="sm:col-span-3">
        <Rotulo id={`${idBase}-tipo`} rotulo={t('reuniao.campo')} ajuda={ajuda}>
          <Selecao
            id={`${idBase}-tipo`}
            opcoes={TIPOS.map((x) => ({ valor: x, rotulo: off[x] ? t('reuniao.indisponivel', { nome: t(`reuniao.${x}`) }) : t(`reuniao.${x}`), desabilitado: off[x] }))}
            value={tipo}
            onChange={(e) => aoTipo(e.target.value as TipoReuniao)}
          />
        </Rotulo>
      </div>
      {tipo === 'link' && (
        <div className="sm:col-span-3">
          <Rotulo id={`${idBase}-link`} rotulo={t('campos.linkReuniao')} ajuda={t('campos.linkAjuda')} obrigatorio>
            <Entrada id={`${idBase}-link`} type="url" inputMode="url" maxLength={2000} value={link} onChange={(e) => aoLink(e.target.value)} />
          </Rotulo>
        </div>
      )}
    </>
  );
}

/** Corpo da reunião para a API: o link só vai com "Link próprio" (Meet e sala são preenchidos pelo servidor). */
export const reuniaoParaApi = (tipo: TipoReuniao, link: string) => ({ reuniao_tipo: tipo, ...(tipo === 'link' ? { link_reuniao: link.trim() || null } : {}) });

/** Lote 19 (12e): servidor de vídeo da sala própria (padrão do workspace: só quem vê todas as empresas grava). */
export function SalaReuniaoAgenda() {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const pode = podeNoWorkspace(s, 'agenda.configurar');
  const q = useConfigReuniao();
  const [base, setBase] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    if (q.data) setBase(q.data.sala_base ?? '');
  }, [q.data]);

  const salvar = useMutation({
    mutationFn: () => api<{ sala_base: string | null }>('PUT', '/agenda/reuniao', { sala_base: base.trim() || null }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: CHAVE_REUNIAO });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const alterado = (base.trim() || null) !== (q.data?.sala_base ?? null);

  return (
    <Cartao
      titulo={t('reuniao.servidorTitulo')}
      acao={
        pode && (
          <Botao variante="secundario" tamanho="sm" disabled={!alterado} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        )
      }
    >
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : (
        <div className="flex flex-col gap-2">
          <Rotulo id="ag-sala-base" rotulo={t('reuniao.servidorCampo')} ajuda={t('reuniao.servidorAjuda')} erro={erro ?? undefined}>
            <Entrada id="ag-sala-base" type="url" inputMode="url" maxLength={300} placeholder="https://" disabled={!pode} value={base} onChange={(e) => setBase(e.target.value)} />
          </Rotulo>
        </div>
      )}
    </Cartao>
  );
}
